/**
 * Теги пинов: глобальный словарь разметки референсов (`/api/tags`, `/api/pins/*`).
 *
 * Тег вешается на ПИН (карточку в сетке), а не на файл, и словарь один на всех:
 * тег описывает картинку («крупный план ткани»), а не чью-то папку. Ручки меток
 * пакетные — одиночное действие из просмотра пина шлёт пакет из одного.
 *
 * «В библиотеку» пачкой идёт порциями по `LIBRARY_CHUNK`: у пина вне «лучших»
 * оригинал качается с Pinterest (~1 с на пин), и порция держит запрос коротким,
 * а ход виден человеку.
 */
import { apiFetch } from "@/lib/api";
import { post, type Pin } from "@/lib/trends";

export type Tag = {
  id: number;
  name: string;
  pins: number;
  items: number;
  author_name: string;
  date_create: string;
  /** Автотег: русский промпт, его английский перевод, группа, порог (null — умолчание сервиса). */
  prompt: string | null;
  prompt_en: string | null;
  group_id: number | null;
  group_name: string | null;
  threshold: number | null;
  /** Сколько референсов с меткой: ручной, автоматической, снятой человеком; согласие автотега с ручными. */
  manual: number;
  auto: number;
  removed: number;
  agree: number | null;
};
export type TagLite = { id: number; name: string; created: boolean };
export type TagGroup = { id: number; name: string; tags: number };

export const tagKeys = { list: ["tags"] as const, groups: ["tag-groups"] as const };

export type TagsData = { tags: Tag[]; autotag_pending: number };
export const loadTags = () => apiFetch<TagsData>("/api/tags");

/** Только что заведённый тег для оптимистичного дополнения кэша словаря. */
export const blankTag = (id: number, name: string): Tag => ({
  id,
  name,
  pins: 0,
  items: 0,
  author_name: "",
  date_create: "",
  prompt: null,
  prompt_en: null,
  group_id: null,
  group_name: null,
  threshold: null,
  manual: 0,
  auto: 0,
  removed: 0,
  agree: null,
});
export const loadTagGroups = () => apiFetch<{ groups: TagGroup[] }>("/api/tag-groups");

export type AutotagRow = { id: number; score: number; asset_path: string; rival: string | null };
export type AutotagPreview = {
  prompt_en: string;
  total: number;
  scores: number[];
  top: AutotagRow[];
  edge: { above: AutotagRow[]; below: AutotagRow[] };
};
export type AutotagReportRow = {
  tag_id: number;
  name: string;
  active: number;
  added: number;
  dropped: number;
  removed: number;
};
export type AutotagPreviewBody = {
  prompt: string;
  prompt_en: string | null;
  group_id: number | null;
  tag_id: number | null;
  library_id: number | null;
  threshold: number;
};
export type AutotagSaveBody = {
  prompt: string;
  prompt_en: string | null;
  group: number | string | null;
  threshold: number;
  exclude_ids: number[];
};

export const previewAutotag = (body: AutotagPreviewBody) => post<AutotagPreview>("/api/tags/autotag/preview", body);
export const saveAutotag = (id: number, body: AutotagSaveBody) =>
  post<{ tag: Tag; report: AutotagReportRow[] }>(`/api/tags/${id}/autotag`, body);
export const resetAutotag = (id: number) =>
  apiFetch<{ tag_id: number; removed: number }>(`/api/tags/${id}/autotag`, { method: "DELETE" });
export const runAutotags = () => post<{ report: AutotagReportRow[] }>("/api/tags/autotag/run", {});
export const createTag = (name: string) => post<TagLite>("/api/tags", { name });
export const renameTag = (id: number, name: string) =>
  post<{ id: number; name: string }>(`/api/tags/${id}/rename`, { name });
export const deleteTag = (id: number) =>
  apiFetch<{ deleted: number; unlinked: number; rerun_error?: string }>(`/api/tags/${id}`, { method: "DELETE" });

/** Тег по id или по имени (нет такого — сервис заведёт). */
export type TagBy = { tagId?: number; name?: string };

export const tagPins = (pinIds: number[], by: TagBy) =>
  post<{ tag: TagLite; added: number; already: number }>("/api/pins/tag", {
    pin_ids: pinIds,
    tag_id: by.tagId ?? null,
    name: by.name ?? "",
  });
export const untagPins = (pinIds: number[], tagId: number) =>
  post<{ removed: number }>("/api/pins/untag", { pin_ids: pinIds, tag_id: tagId });

export type BulkResult = { added: number; already: number; errors: string[] };
export const LIBRARY_CHUNK = 10;

/** Пины в библиотеку порциями, последовательно; `onProgress` — сколько отправлено из скольких. */
export async function pinsToLibrary(
  pinIds: number[],
  libraryId: number,
  onProgress?: (done: number, total: number) => void,
): Promise<BulkResult> {
  const out: BulkResult = { added: 0, already: 0, errors: [] };
  onProgress?.(0, pinIds.length);
  for (let i = 0; i < pinIds.length; i += LIBRARY_CHUNK) {
    const chunk = pinIds.slice(i, i + LIBRARY_CHUNK);
    const got = await post<BulkResult>("/api/pins/to-library", { pin_ids: chunk, library_id: libraryId });
    out.added += got.added;
    out.already += got.already;
    out.errors.push(...got.errors);
    onProgress?.(Math.min(i + chunk.length, pinIds.length), pinIds.length);
  }
  return out;
}

/** Цвет тега по id: золотой угол разводит соседние id по кругу оттенков. */
export const tagHue = (id: number) => Math.round((id * 137.508) % 360);
/** Тёмная плашка с белым текстом — читается и на светлой, и на тёмной теме. */
export const tagStyle = (id: number) => ({ background: `oklch(48% 0.13 ${tagHue(id)})`, color: "#fff" });

/** Локальный патч кэша экрана: у затронутых пинов добавить/убрать тег, без перезапроса 400 КБ. */
export function patchPinTags<T extends { pins: Pin[] }>(
  data: T | undefined,
  pinIds: ReadonlySet<number>,
  tagId: number,
  add: boolean,
): T | undefined {
  if (!data) return data;
  return {
    ...data,
    pins: data.pins.map((p) => {
      if (!pinIds.has(p.id)) return p;
      const has = p.tag_ids.includes(tagId);
      if (add === has) return p;
      return {
        ...p,
        tag_ids: add ? [...p.tag_ids, tagId].sort((a, b) => a - b) : p.tag_ids.filter((t) => t !== tagId),
      };
    }),
  };
}
